import { useMemo, useRef, useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { COUNTRIES, countryByCode } from "@fohet/caravan-booker-shared/countries"
import { cn } from "@/lib/utils"

// Codes floated to the top of the list — the nationalities this platform sees
// most. Everything else follows alphabetically by name.
const COMMON = ["CZ", "SK", "PL", "DE", "AT", "UA", "SE", "NO", "NL", "GB"]

interface CountrySelectProps {
    /** Selected ISO 3166-1 alpha-2 code, or "" when nothing is chosen. */
    value: string
    onChange: (code: string) => void
    placeholder?: string
}

// Lightweight searchable country picker. Deliberately built over a plain
// <input> + filtered list rather than pulling in a combobox dependency (see
// docs/ubyport-todo.md §9). Mobile-first: full-width, 16px input font so iOS
// doesn't zoom on focus, scrollable dropdown, generous touch targets. Country
// names come from the shared ISO list for now; localized labels arrive with
// the Staty catalog later.
export const CountrySelect = ({ value, onChange, placeholder }: CountrySelectProps) => {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState("")
    const containerRef = useRef<HTMLDivElement>(null)

    const selected = value ? countryByCode(value) : undefined

    // Diacritics-insensitive substring match over name + code.
    const normalize = (s: string) =>
        s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase()

    const ordered = useMemo(() => {
        const rank = (code: string) => {
            const i = COMMON.indexOf(code)
            return i === -1 ? COMMON.length : i
        }
        return [...COUNTRIES].sort((a, b) => {
            const ra = rank(a.code)
            const rb = rank(b.code)
            if (ra !== rb) return ra - rb
            return a.name.localeCompare(b.name)
        })
    }, [])

    const filtered = useMemo(() => {
        const q = normalize(query.trim())
        if (!q) return ordered
        return ordered.filter(
            (c) => normalize(c.name).includes(q) || c.code.toLowerCase().includes(q) || c.alpha3.toLowerCase().includes(q),
        )
    }, [ordered, query])

    // Close on outside click.
    useEffect(() => {
        if (!open) return
        const onDown = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false)
                setQuery("")
            }
        }
        document.addEventListener("mousedown", onDown)
        return () => document.removeEventListener("mousedown", onDown)
    }, [open])

    const pick = (code: string) => {
        onChange(code)
        setOpen(false)
        setQuery("")
    }

    return (
        <div ref={containerRef} className="relative w-full">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className={cn(
                    "flex h-11 w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-3",
                    "text-left text-base font-bold text-slate-900",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand",
                )}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <span className={cn(!selected && "text-slate-400")}>
                    {selected ? `${selected.name} (${selected.code})` : (placeholder ?? t("components.countrySelect.placeholder"))}
                </span>
                <span className="ml-2 shrink-0 text-slate-400">▾</span>
            </button>

            {open && (
                <div className="absolute z-50 mt-1 w-full rounded-xl border border-slate-200 bg-white shadow-xl">
                    <div className="p-2">
                        <input
                            autoFocus
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={t("components.countrySelect.search")}
                            // 16px (text-base) avoids the iOS focus zoom.
                            className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-base font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                        />
                    </div>
                    <ul role="listbox" className="max-h-60 overflow-y-auto pb-1">
                        {filtered.length === 0 ? (
                            <li className="px-3 py-2 text-sm font-bold text-slate-400">
                                {t("components.countrySelect.noMatch")}
                            </li>
                        ) : (
                            filtered.map((c) => (
                                <li key={c.code}>
                                    <button
                                        type="button"
                                        role="option"
                                        aria-selected={c.code === value}
                                        onClick={() => pick(c.code)}
                                        className={cn(
                                            "flex min-h-11 w-full items-center justify-between px-3 py-2 text-left text-sm font-bold",
                                            c.code === value ? "bg-brand/10 text-slate-900" : "text-slate-700 hover:bg-slate-50",
                                        )}
                                    >
                                        <span>{c.name}</span>
                                        <span className="ml-2 shrink-0 text-xs font-black uppercase text-slate-400">{c.code}</span>
                                    </button>
                                </li>
                            ))
                        )}
                    </ul>
                </div>
            )}
        </div>
    )
}
